(() => {
  class MaramePages extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      this.reveals?.disconnect();
      if (this.frame) cancelAnimationFrame(this.frame);
      this.frame = null;
      this.controller = new AbortController();
      const signal = this.controller.signal;
      this.reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.cleanPolicySpacing();
      const reveals = this.querySelectorAll('[data-page-reveal]');
      if (!this.reduced && 'IntersectionObserver' in window) {
        this.setAttribute('data-pages-motion', '');
        this.reveals = new IntersectionObserver((entries) => {
          for (const entry of entries) if (entry.isIntersecting) {
            entry.target.setAttribute('data-visible', '');
            this.reveals.unobserve(entry.target);
          }
        }, { threshold: 0.1 });
        reveals.forEach((node) => this.reveals.observe(node));
      }
      this.addEventListener('shopify:block:select', (event) => {
        const item = event.target.closest('[data-faq-item]');
        if (item) {
          this.search.value = '';
          this.activeCategory = item.dataset.category;
          this.updateQuestions();
          item.querySelector('details').open = true;
        }
        const panel = event.target.closest('[data-story-panel]');
        panel?.scrollIntoView({ behavior: 'instant', block: 'start' });
        const policy = event.target.closest('[data-policy-group]');
        if (policy?.dataset.policyGroup) this.activatePolicy(policy.dataset.policyGroup);
        event.target.closest('[data-page-reveal]')?.setAttribute('data-visible', '');
      }, { signal });
      if (this.dataset.mode === 'faq') this.setupQuestions(signal);
      if (this.dataset.mode === 'policy') this.setupPolicy(signal);
      if (this.matches('[data-marame-story]')) this.setupStory(signal);
    }
    cleanPolicySpacing() {
      this.querySelectorAll('.marame-page-body p').forEach(paragraph => {
        if (!paragraph.textContent.trim() && !paragraph.querySelector('img, picture, video, audio, iframe, svg, canvas, input, button, a')) paragraph.hidden = true;
      });
    }
    setupPolicy(signal) {
      this.policyNav = this.querySelector('[data-policy-nav]');
      this.policyLinks = Array.from(this.querySelectorAll('[data-policy-link]'));
      this.policyToggles = Array.from(this.querySelectorAll('[data-policy-toggle]'));
      this.policyDesktop = window.matchMedia('(min-width: 1024px)');
      this.policyBlocks = Array.from(this.querySelectorAll('[data-policy-group]'));
      if (!this.policyNav || this.policyLinks.length < 2) return;
      this.policyNav.setAttribute('role', 'tablist');
      this.policyNav.setAttribute('aria-orientation', 'vertical');
      this.policyLinks.forEach((link, index) => {
        const id = link.hash.slice(1);
        const panel = this.policyBlocks.find(block => block.id === id);
        link.setAttribute('role', 'tab');
        link.setAttribute('aria-controls', id);
        panel?.setAttribute('data-policy-chapter', '');
        link.addEventListener('click', event => {
          if (event.button > 0 || event.metaKey || event.ctrlKey || event.altKey || event.shiftKey) return;
          event.preventDefault();
          this.activatePolicy(id, { hash: true });
        }, { signal });
        link.addEventListener('keydown', event => {
          if (event.key === ' ') {
            event.preventDefault();
            this.activatePolicy(id, { hash: true });
            return;
          }
          const last = this.policyLinks.length - 1;
          const next = { ArrowDown: (index + 1) % (last + 1), ArrowUp: (index + last) % (last + 1), Home: 0, End: last }[event.key];
          if (next === undefined) return;
          event.preventDefault();
          const target = this.policyLinks[next];
          this.activatePolicy(target.hash.slice(1), { hash: true });
          target.focus({ preventScroll: true });
        }, { signal });
      });
      this.policyToggles.forEach((toggle, index) => {
        const id = toggle.dataset.policyToggle;
        const contents = this.policyBlocks.filter(block => block.dataset.policyGroup === id).map(block => block.querySelector('[data-policy-content]')).filter(Boolean);
        toggle.setAttribute('aria-controls', contents.map(content => content.id).join(' '));
        toggle.addEventListener('click', () => {
          this.activatePolicy(this.activePolicyId === id ? '' : id, { hash: true });
          toggle.focus({ preventScroll: true });
          toggle.closest('[data-policy-group]')?.scrollIntoView({ behavior: this.reduced ? 'instant' : 'smooth', block: 'start' });
        }, { signal });
        toggle.addEventListener('keydown', event => {
          const last = this.policyToggles.length - 1;
          const next = { ArrowDown: (index + 1) % (last + 1), ArrowUp: (index + last) % (last + 1), Home: 0, End: last }[event.key];
          if (next === undefined) return;
          event.preventDefault();
          this.policyToggles[next].focus();
        }, { signal });
      });
      this.setAttribute('data-policy-enhanced', '');
      this.policyDesktop.addEventListener('change', () => {
        const active = document.activeElement;
        const focusId = this.policyLinks.find(link => link === active)?.hash.slice(1) || this.policyToggles.find(toggle => toggle === active)?.dataset.policyToggle;
        this.activatePolicy(this.activePolicyId || this.lastPolicyId || this.policyLinks[0].hash.slice(1));
        if (focusId) {
          const target = this.policyDesktop.matches ? this.policyLinks.find(link => link.hash === `#${this.activePolicyId}`) : this.policyToggles.find(toggle => toggle.dataset.policyToggle === focusId);
          target?.focus({ preventScroll: true });
        }
      }, { signal });
      const fromHash = () => {
        let id;
        try { id = decodeURIComponent(window.location.hash.slice(1)); } catch { return; }
        const block = this.policyBlocks.find(node => node.id === id || node.contains(document.getElementById(id)));
        if (block?.dataset.policyGroup) this.activatePolicy(block.dataset.policyGroup);
      };
      this.activatePolicy(this.policyLinks[0].hash.slice(1));
      fromHash();
      window.addEventListener('hashchange', fromHash, { signal });
    }
    activatePolicy(id, { hash = false, focus = false } = {}) {
      const desktop = this.policyDesktop?.matches !== false;
      if (id ? !this.policyLinks?.some(link => link.hash === `#${id}`) : desktop) return;
      this.activePolicyId = id;
      if (id) this.lastPolicyId = id;
      this.policyBlocks.forEach(block => {
        const selected = !block.dataset.policyGroup || block.dataset.policyGroup === id;
        const toggle = block.querySelector('[data-policy-toggle]');
        const heading = block.querySelector('[data-policy-heading]');
        const content = block.querySelector('[data-policy-content]');
        block.hidden = !selected && (desktop || !toggle);
        if (content) content.hidden = !desktop && !selected;
        if (toggle) toggle.hidden = desktop;
        if (heading) heading.hidden = !desktop;
        if (toggle) {
          if (desktop) {
            block.setAttribute('role', 'tabpanel');
            block.setAttribute('aria-labelledby', this.policyLinks.find(link => link.hash === `#${block.id}`).id);
            block.setAttribute('tabindex', '0');
          } else {
            block.removeAttribute('role');
            block.removeAttribute('aria-labelledby');
            block.removeAttribute('tabindex');
          }
        }
        if (!block.hidden) block.setAttribute('data-visible', '');
      });
      this.policyLinks.forEach(link => {
        const selected = link.hash === `#${id}`;
        link.setAttribute('aria-selected', String(selected));
        link.tabIndex = selected ? 0 : -1;
      });
      this.policyToggles.forEach(toggle => toggle.setAttribute('aria-expanded', String(toggle.dataset.policyToggle === id)));
      if (hash && id && window.location.hash !== `#${id}`) window.history.replaceState(window.history.state, '', `#${id}`);
      if (focus) {
        const panel = this.policyBlocks.find(block => block.id === id);
        panel?.scrollIntoView({ behavior: this.reduced ? 'instant' : 'smooth', block: 'start' });
        (desktop ? panel : panel?.querySelector('[data-policy-toggle]'))?.focus({ preventScroll: true });
      }
    }
    setupQuestions(signal) {
      this.search = this.querySelector('[data-faq-search]');
      this.clearButton = this.querySelector('[data-faq-clear]');
      this.items = Array.from(this.querySelectorAll('[data-faq-item]'));
      this.tabs = Array.from(this.querySelectorAll('[data-faq-category]'));
      this.activeCategory = this.tabs[0]?.dataset.faqCategory ?? '';
      this.querySelectorAll('[data-faq-controls]').forEach(node => node.hidden = false);
      this.search.addEventListener('input', () => this.updateQuestions(), { signal });
      this.clearButton.addEventListener('click', () => {
        this.search.value = '';
        this.updateQuestions();
        this.search.focus();
      }, { signal });
      this.tabs.forEach(tab => tab.addEventListener('click', () => {
        this.activeCategory = tab.dataset.faqCategory;
        this.updateQuestions();
      }, { signal }));
      this.updateQuestions();
    }
    updateQuestions() {
      const query = this.search.value.trim().toLocaleLowerCase();
      const searching = query.length >= 2;
      let visible = 0;
      for (const item of this.items) {
        const match = searching ? item.querySelector('details').textContent.toLocaleLowerCase().includes(query) : item.dataset.category === this.activeCategory;
        item.hidden = !match;
        item.querySelector('.marame-pages__question-category').hidden = !searching;
        if (match) visible += 1;
        else item.querySelector('details').open = false;
      }
      this.tabs.forEach(tab => tab.setAttribute('aria-pressed', String(tab.dataset.faqCategory === this.activeCategory)));
      this.querySelector('.marame-pages__categories').hidden = searching;
      this.clearButton.hidden = !this.search.value;
      const status = this.querySelector('[data-faq-results]');
      status.hidden = !searching;
      status.textContent = `${visible} ${visible === 1 ? (this.dataset.resultLabel || this.dataset.resultsLabel) : this.dataset.resultsLabel} “${this.search.value.trim()}”`;
      this.querySelector('[data-faq-empty]').hidden = visible !== 0;
    }
    setupStory(signal) {
      this.panels = Array.from(this.querySelectorAll('[data-story-panel]'));
      this.dots = Array.from(this.querySelectorAll('.marame-story__progress span'));
      this.setAttribute('data-ready', '');
      this.updateStory = () => {
        this.frame = null;
        const header = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--marame-header-height')) || 52;
        let active = 0, distance = Infinity;
        this.panels.forEach((panel, index) => {
          const rect = panel.getBoundingClientRect();
          const score = rect.top <= header && rect.bottom > header ? 0 : Math.abs(rect.top - header);
          if (score < distance) { distance = score; active = index; }
        });
        this.dots.forEach((dot, index) => {
          if (index === active) dot.setAttribute('aria-current', 'true');
          else dot.removeAttribute('aria-current');
        });
        this.dataset.activeTone = this.panels[active]?.dataset.tone || 'dark';
      };
      const schedule = () => { if (!this.frame) this.frame = requestAnimationFrame(this.updateStory); };
      window.addEventListener('scroll', schedule, { passive: true, signal });
      window.addEventListener('resize', schedule, { passive: true, signal });
      this.updateStory();
    }
    disconnectedCallback() {
      this.controller?.abort();
      this.reveals?.disconnect();
      if (this.frame) cancelAnimationFrame(this.frame);
      this.removeAttribute('data-pages-motion');
    }
  }
  if (!customElements.get('marame-help')) customElements.define('marame-help', class extends MaramePages {});
  if (!customElements.get('marame-story')) customElements.define('marame-story', class extends MaramePages {});
})();
